import { environment } from "@environments/environment";

/**
 * config
 */
export interface ConfigParameters {
    /** 
     * 広告向けの機能をすべて無効にするには、allow_google_signals を false に設定します
     * @defaultValue true
     */
    allow_google_signals: boolean; // default: true
    /**
     * 広告のパーソナライズ機能を無効にするには、false に設定します。
     * @defaultValue true
     */
    allow_ad_personalization_signals: boolean;
    /**
     * A/B テストやコンテンツ ターゲット広告で使用します。campaign_content を使用して、同じ URL を参照する広告とリンクを区別します。
     * @defaultValue undefined
     */
    campaign_content:string|undefined;
    /**
     * この参照が参照しているキャンペーンを識別するために使用されます。campaign_id を使用して、特定のキャンペーンを識別できます。
     * @defaultValue undefined
     */
    campaign_id:string|undefined;
    /**
     * campaign_medium を使用して、メールやクリック単価などのメディアを識別します。
     * @defaultValue undefined
     */
    campaign_medium:string|undefined;
    /**
     * キーワード分析で使用します。campaign_name を使用して、特定商品のプロモーション キャンペーンやまたは戦略的キャンペーンを識別します。
     * @defaultValue undefined
     */
    campaign_name:string|undefined;
    /**
     * campaign_source を使用して、検索エンジンやニュースレター名などの参照元を識別します。
     * @defaultValue undefined
     */
    campaign_source:string|undefined;
    /**
     * 有料検索で使用します。campaign_term を使用して、有料検索広告のキーワードをトラッキングします。
     * @defaultValue undefined
     */
    campaign_term:string|undefined;
    /**
     * ブラウザのインスタンスを仮名で識別します。
     * デフォルトでは、この値はファーストパーティのアナリティクス Cookie として 2 年間の有効期限付きで保管されます。
     * @defaultValue ユーザーごとにランダムに生成された値。
     */
    client_id: string;
    /**
     * コンテンツ グループ
     * @see: https://support.google.com/analytics/answer/11523339?hl=ja
     */
    content_group:string
    /**
     * cookie_domain
     * アナリティクス Cookie の保管に使用するドメインを指定します。
     * ドメインを指定せずに Cookie を設定するには、'none' に設定します。
     * Cookie をトップレベル ドメインと 1 つのサブドメイン（eTLD +1）にドメイン指定するには、'auto'（デフォルト値）に設定します。
     * たとえば、cookie_domain が 'auto' に設定されている場合、https://example.com はドメインに example.com を使用し、https://subdomain.example.com もドメインに example.com を使用します。
     * @defaultValue 'auto'
     */
    cookie_domain: 'auto'|'none'|string;
    /**
     * ヒットが Google アナリティクスに送信されるたびに、Cookie の有効期限はその時点の時刻と cookie_expires フィールドの値を足した値に更新されます。 
     * つまり、デフォルト値の 2 年（63,072,000 秒）を使用しており、ユーザーが毎月サイトを訪問する場合、Cookie が期限切れになることはありません。
     * cookie_expires を 0（ゼロ）秒に設定すると Cookie がセッション ベースになり、現在のブラウザ セッションが終了した時点で期限切れとなります
     * @defaultValue 63072000
     */
    cookie_expires: number;
    /**
     * 設定すると Cookie に追加フラグを付加します。フラグはセミコロンで区切る必要があります。設定するフラグの例については、新しい Cookie を作成するをご覧ください。
     * @see: https://developer.mozilla.org/ja/docs/Web/API/Document/cookie
     */
    cookie_flags: string;
    /**
     * アナリティクス Cookie の保管に使用するサブパスを指定します。
     * @defaultValue '/'
     */
    cookie_path:string;
    /**
     * アナリティクス Cookie の名前の先頭に付加する接頭辞を指定します。
     */
    cookie_prefix: string;
    /**
     * cookie_update を true に設定すると、gtag.js はページが読み込まれるたびに Cookie を更新します。
     * つまり、サイトへの最新の訪問に基づいて Cookie の有効期限が更新されます。
     * たとえば、Cookie の有効期限が 1 週間に設定されており、ユーザーが 5 日ごとに同じブラウザを使用して訪問した場合、Cookie の有効期限は訪問ごとに更新されるため、有効期限は実質的に切れることがなくなります。
     * 
     * false に設定すると、Cookie はページの読み込みごとには更新されません。
     * つまり、ユーザーが最初にサイトにアクセスしたときに基づいて Cookie の有効期限が決まります。
     * 
     * @defaultValue true
     */
    cookie_update: boolean;
    /**
     * true に設定すると、参照 URL がトラフィック ソースとして表示されなくなります。このフィールドの使用に適したケース
     * @see https://support.google.com/analytics/answer/10327750?hl=ja#set-parameter
     * @defaultValue false
     */
    ignore_referrer: boolean;
    /**
     * ユーザーの言語設定を指定します。デフォルトは、ユーザーの navigator.language 値です。
     * @defaultValue navigator.language
     */
    language: string;
    /**
     * ページの完全な URL を指定します。デフォルトは、ユーザーの document.location 値です。
     * @defualtValue document.location 
     * 文字数制限:最大1000文字
     */
    page_location:string;
    /**
     * ページにトラフィックを送り込んだ参照ソースを指定します。この値はトラフィック ソースの算出にも使用され、値の形式は URL となります。 デフォルトは、ユーザーの document.referrer 値です。
     * @defaultValue document.referrer
     * 文字数制限:420文字
     */
    page_referrer:string;
    /**
     * ページまたはドキュメントのタイトルです。デフォルトは、ユーザーの document.title 値です。
     * @defaultValue document.title
     * 文字数制限:最大300文字
     */
    page_title: string;
    /**
     * false に設定すると、デフォルトのスニペットが page_view を送信できなくなります。
     * @defaultValue true
     */
    send_page_view: boolean;
    /**
     * 画面の解像度を指定します。x で区切られた 2 つの正の整数にする必要があります。
     * たとえば、800 × 600 ピクセルの画面の場合、値は 800x600 になります。
     * ユーザーの window.screen 値から計算されます
     * @defaultValue window.screen
     */
    screen_resolution:string;
    /**
     * サイトの所有者またはライブラリの利用者により提供された、ユーザーの既知の識別子を指定します。
     * ユーザー ID として個人情報を使うことはできません。
     * また、Google アナリティクス Cookie など、アナリティクスのストレージにこの値を残すことはできません。
     */
    user_id:string;
    /**
     * ユーザー プロパティとは、言語設定や地域など、ユーザーベースのセグメントを表現するのに使用される属性を指します。
     * プロジェクトごとに、最大 25 個のユーザー プロパティを追加設定できます。
     */
    user_properties:Record<string,JSONValue>
}
/**
 * データのグループ化
 * https://developers.google.com/tag-platform/gtagjs/routing?hl=ja
 */
export interface ConfigOptions extends Partial<ConfigParameters> {
    groups?: 'default' | string;
}
/**
 * グローバルな設定
 */
export const config = (streamId:string,config?:ConfigOptions) => window.gtag('config',streamId,config);
/**
 * 単一ストリームに設定
 */
export const set = <K extends keyof ConfigParameters,V extends ConfigParameters[K]>(streamId:string,fileldName:K,value:V) => window.gtag('set',fileldName,value);
/**
 * https://developers.google.com/tag-platform/gtagjs/reference?hl=ja#get
 */
export type FieldName = 'client_id' | 'session_id' | 'gclid';
/**
 * 
 */
export type GetCallback = ($_:string|undefined) => void;
export const get = (fieldName:FieldName,callback:GetCallback,streamId=environment.analyticsId) => window.gtag('get',streamId,fieldName,callback);
